import { USER_ROLES } from './constants';

const normalizeRoles = (rolesOrRole) => {
  if (Array.isArray(rolesOrRole)) {
    return rolesOrRole;
  }

  if (typeof rolesOrRole === 'string' && rolesOrRole.trim().length > 0) {
    return [rolesOrRole];
  }

  return [];
};

/**
 * Ottiene l'etichetta display di un ruolo
 * @param {string} role - Il ruolo dell'utente
 * @returns {string} L'etichetta del ruolo
 */
export const getRoleLabel = (role) => {
  return role || 'Sconosciuto';
};

/**
 * Ottiene etichette leggibili per una lista di ruoli
 * @param {string[]|string} rolesOrRole - Lista ruoli o ruolo singolo
 * @returns {string[]} Elenco etichette
 */
export const getRoleLabels = (rolesOrRole) => {
  return normalizeRoles(rolesOrRole).map(getRoleLabel);
};

/**
 * Verifica la presenza di un ruolo
 * @param {string[]|string} rolesOrRole - Lista ruoli o ruolo singolo
 * @param {string} role - Ruolo da verificare
 * @returns {boolean}
 */
export const hasRole = (rolesOrRole, role) => {
  if (!role) return false;
  const target = role.toLowerCase();
  return normalizeRoles(rolesOrRole).some(r => typeof r === 'string' && r.toLowerCase() === target);
};

/**
 * Verifica la presenza di almeno un ruolo
 * @param {string[]|string} rolesOrRole - Lista ruoli o ruolo singolo
 * @param {string[]} roles - Ruoli ammessi
 * @returns {boolean}
 */
export const hasAnyRole = (rolesOrRole, roles) => {
  const userRoles = normalizeRoles(rolesOrRole);
  return Array.isArray(roles) && roles.some(role => userRoles.includes(role));
};

/**
 * Verifica se un utente è un atleta
 * @param {string} role - Il ruolo dell'utente
 * @returns {boolean} True se è un atleta
 */
export const isAthlete = (rolesOrRole) => {
  return hasRole(rolesOrRole, USER_ROLES.ATHLETE);
};

/**
 * Verifica se un utente è un amministratore
 * @param {string} role - Il ruolo dell'utente
 * @returns {boolean} True se è un amministratore
 */
export const isAdmin = (rolesOrRole) => {
  return hasRole(rolesOrRole, USER_ROLES.ADMIN);
};

/**
 * Verifica se un utente è un socio
 * @param {string} role - Il ruolo dell'utente
 * @returns {boolean} True se è un socio
 */
export const isMember = (rolesOrRole) => {
  return hasRole(rolesOrRole, USER_ROLES.MEMBER);
};

/**
 * Tenta di estrarre la data di nascita dal codice fiscale italiano
 * @param {string} fiscalCode - Codice fiscale
 * @returns {Date|null} Data di nascita o null se non valido
 */
export const parseBirthDateFromFiscalCode = (fiscalCode) => {
  if (!fiscalCode || typeof fiscalCode !== 'string' || fiscalCode.trim().length < 11) {
    return null;
  }
  const clean = fiscalCode.trim().toUpperCase();
  const match = clean.match(/^[A-Z]{6}(\d{2})([A-EHLMPR-T])(\d{2})/);
  if (!match) return null;

  const yearPart = parseInt(match[1], 10);
  const monthChar = match[2];
  let dayPart = parseInt(match[3], 10);

  if (dayPart > 40) {
    dayPart -= 40;
  }
  if (dayPart < 1 || dayPart > 31) return null;

  const monthMap = {
    A: 0, B: 1, C: 2, D: 3, E: 4, H: 5,
    L: 6, M: 7, P: 8, R: 9, S: 10, T: 11
  };
  const month = monthMap[monthChar];
  if (month === undefined) return null;

  const currentYear = new Date().getFullYear();
  const currentCentury = Math.floor(currentYear / 100) * 100;
  const currentTwoDigit = currentYear % 100;

  const fullYear = yearPart <= currentTwoDigit + 5
    ? currentCentury + yearPart
    : (currentCentury - 100) + yearPart;

  const date = new Date(Date.UTC(fullYear, month, dayPart));
  if (isNaN(date.getTime())) return null;
  return date;
};

/**
 * Calcola l'età di un utente dalla data di nascita (o fallback da codice fiscale)
 * @param {string|Date} dateOfBirth - Data di nascita
 * @param {string} [fiscalCode] - Codice fiscale per fallback
 * @returns {number|null} Età in anni o null se data non valida
 */
export const calculateAge = (dateOfBirth, fiscalCode = null) => {
  let birthDate = dateOfBirth ? new Date(dateOfBirth) : null;
  if (!birthDate || isNaN(birthDate.getTime())) {
    birthDate = parseBirthDateFromFiscalCode(fiscalCode);
  }
  if (!birthDate || isNaN(birthDate.getTime())) return null;
  
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  
  return age;
};

/**
 * Verifica se un utente è minorenne
 * @param {string|Date} dateOfBirth - Data di nascita
 * @param {string} [fiscalCode] - Codice fiscale per fallback
 * @returns {boolean} True se minorenne
 */
export const isMinor = (dateOfBirth, fiscalCode = null) => {
  const age = calculateAge(dateOfBirth, fiscalCode);
  return age !== null && age < 18;
};

/**
 * Formatta il nome completo di un utente
 * @param {string} firstName - Nome
 * @param {string} lastName - Cognome
 * @returns {string} Nome completo formattato
 */
export const formatFullName = (firstName, lastName) => {
  const first = firstName?.trim() || '';
  const last = lastName?.trim() || '';
  return `${first} ${last}`.trim();
};

/**
 * Trova la subscription attiva per un utente in un gruppo a una data specifica
 * @param {Array} subscriptions - Array di subscription del gruppo
 * @param {string} userId - ID dell'utente
 * @param {string|Date} date - Data da verificare
 * @returns {Object|null} Subscription attiva o null
 */
export const findActiveSubscription = (subscriptions, userId, date) => {
  if (!subscriptions || !userId || !date) {
    return null;
  }

  const checkDate = new Date(date);
  const userSubscription = subscriptions.find(sub => {
    const subUserId = sub.user?.id || sub.userId;
    if (subUserId !== userId) return false;
    if (!sub.offer) return false;
    if (new Date(sub.startDate) > checkDate || new Date(sub.endDate) < checkDate) return false;

    if (sub.hasInstallments && sub.installments && sub.installments.length > 0) {
      const hasOverdueUnpaidInstallment = sub.installments.some(inst => {
        if (inst.paymentDate) return false;
        return new Date(inst.dueDate) < checkDate;
      });
      if (hasOverdueUnpaidInstallment) return false;
    }

    return true;
  });

  return userSubscription || null;
};

/**
 * Calcola gli ingressi per una subscription pay-per-use
 * @param {Object} subscription - Oggetto subscription
 * @param {number} attendanceCount - Numero di presenze registrate
 * @returns {Object} Status ingressi con totalEntries, usedEntries, remainingEntries
 */
export const calculateSubscriptionEntries = (subscription, attendanceCount) => {
  if (!subscription || !subscription.offer) {
    return null;
  }

  // For pay-per-use subscriptions, calculate entries
  if (subscription.offer.isPayPerUse || (subscription.offer.totalTickets != null && subscription.offer.totalTickets > 0)) {
    const totalEntries = subscription.offer.totalTickets || 0;
    const usedEntries = attendanceCount + (subscription.usedEntriesAdjustment || 0);
    const remainingEntries = Math.max(0, totalEntries - usedEntries);

    return {
      hasActiveMonthlySubscription: false,
      totalEntries,
      usedEntries,
      remainingEntries
    };
  }

  // Check if it's a monthly subscription
  if (subscription.offer.isMonthly || (subscription.offer.durationMonths != null && subscription.offer.durationMonths > 0)) {
    return {
      hasActiveMonthlySubscription: true,
      totalEntries: 0,
      usedEntries: 0,
      remainingEntries: 0
    };
  }

  return null;
};